import { useMemo, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Sparkles, AlertCircle, FileText } from 'lucide-react';
import {
  Button,
  Dialog,
  DialogShell,
  DialogShellHeader,
  DialogShellBody,
  DialogShellFooter
} from '@bliks-io/ui';
import type {
  ExternalContact,
  PopulatedExternalContact,
  PopulatedSystem,
  PopulatedOrgFunction,
  PopulatedUser,
  ProcessStepRecording,
  System
} from '@bliks-io/contracts';
import { useOrgMembers } from '@/api/queries/useOrgMembers';
import { useSystems } from '@/api/queries/useSystems';
import { useOrgFunctions } from '@/api/queries/useOrgFunctions';
import { useExternalContacts } from '@/api/queries/useExternalContacts';
import { PreviousStepPreview } from '@/components/processes/PreviousStepPreview';
import { AiStepCard, useFieldController } from './StepReviewDialogParts';
import { getPreviousOutputRuleIds } from '../utils/nodeBuilder';
import { useGenerateStepRecordingSummary } from '@/api/mutations/useRecording';

interface StepReviewDialogProps {
  open: boolean;
  recording: ProcessStepRecording;
  aiEnabled: boolean;
  onCancel: () => void;
  onApply: (vals: { name: string; description: string }) => void;
  applyInFlight?: boolean;
  backInFlight?: boolean;
}

type PopulatedAssignee =
  | PopulatedUser
  | PopulatedSystem
  | PopulatedOrgFunction
  | PopulatedExternalContact;

/**
 * Sits between Stop and Finalize: lets the user accept the AI-generated
 * name/description or edit them per-field before locking the recording in.
 * Closing the page is non-destructive — the recording stays in
 * `pending_finalization` and the dialog re-opens on revisit. AI generation
 * is owned here (auto-fired on open, regenerate button on the AI card) so
 * users who navigate back to an already-stopped recording still get a
 * summary.
 */
export function StepReviewDialog({
  open,
  recording,
  aiEnabled,
  onCancel,
  onApply,
  applyInFlight,
  backInFlight
}: StepReviewDialogProps) {
  const { t } = useTranslation();
  const { data: users } = useOrgMembers();
  const { data: orgFunctions } = useOrgFunctions();
  const { data: systems } = useSystems();
  const { data: externalContacts } = useExternalContacts();

  const generate = useGenerateStepRecordingSummary();
  const isGenerating = generate.isPending;
  const aiError = generate.error;

  // Guards against re-firing after an error (where `generatedName` stays
  // empty and `isPending` flips back to false). Reset when the dialog closes
  // so the next open session can fire.
  const hasFiredForSession = useRef(false);

  useEffect(
    function autoGenerateSummaryOnOpen() {
      if (!open) {
        hasFiredForSession.current = false;
        return;
      }
      if (
        aiEnabled &&
        !recording.generatedName &&
        !generate.isPending &&
        !hasFiredForSession.current
      ) {
        hasFiredForSession.current = true;
        generate.mutate(recording._id);
      }
    },
    [open, aiEnabled, recording.generatedName, recording._id, generate]
  );

  const nameField = useFieldController(recording.generatedName ?? '');
  const descriptionField = useFieldController(recording.generatedDescription ?? '');

  // `recording.assignedTo` is an `EntityOrSuggestionRef`. Look up the
  // populated entity locally so the assignee chip can render; suggestion-ref
  // assignees are dropped here (the dialog has nothing to render for them
  // until approval resolves the ref).
  const resolvedAssignee = useMemo<PopulatedAssignee | undefined>(() => {
    const ref = recording.assignedTo;
    if (!ref || ref.type !== 'entity') return undefined;
    switch (recording.assignedToModel) {
      case 'User':
        return (users as PopulatedUser[] | undefined)?.find((u) => u._id === ref.id);
      case 'OrgFunction':
        return (orgFunctions as unknown as PopulatedOrgFunction[] | undefined)?.find(
          (f) => f._id === ref.id
        );
      case 'System':
        return (systems as System[] | undefined)?.find((s) => s._id === ref.id) as
          | PopulatedSystem
          | undefined;
      case 'ExternalContact':
        return (externalContacts as ExternalContact[] | undefined)?.find(
          (c) => c._id === ref.id
        ) as PopulatedExternalContact | undefined;
      default:
        return undefined;
    }
  }, [
    recording.assignedTo,
    recording.assignedToModel,
    users,
    orgFunctions,
    systems,
    externalContacts
  ]);

  const canApply =
    nameField.value.trim().length > 0 && descriptionField.value.trim().length > 0 && !isGenerating;

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onCancel();
      }}
    >
      <DialogShell size="xl">
        <DialogShellHeader
          icon={aiEnabled ? <Sparkles className="h-5 w-5" /> : <FileText className="h-5 w-5" />}
          iconTone="neutral"
          title={t('stepRecording.review.title')}
          subtitle={
            aiEnabled
              ? t('stepRecording.review.subtitle')
              : t('stepRecording.review.subtitleManual')
          }
          onClose={onCancel}
        />
        <DialogShellBody>
          {aiEnabled && aiError ? (
            <div className="flex items-start gap-3 rounded-md border border-destructive/50 bg-destructive/10 px-4 py-3 text-sm text-destructive mb-4">
              <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
              <span>{t('stepRecording.review.aiError')}</span>
            </div>
          ) : null}
          <PreviousStepPreview
            sourceProcessStep={recording.previousStep ?? undefined}
            ruleIds={getPreviousOutputRuleIds(recording.inputs)}
            currentStepSlot={
              <AiStepCard
                assignedTo={resolvedAssignee}
                assignedToModel={recording.assignedToModel}
                aiEnabled={aiEnabled}
                hasGeneratedContent={Boolean(
                  recording.generatedName || recording.generatedDescription
                )}
                isGenerating={isGenerating}
                nameField={nameField}
                descriptionField={descriptionField}
                onRegenerate={() => {
                  hasFiredForSession.current = true;
                  generate.mutate(recording._id);
                }}
              />
            }
          />
        </DialogShellBody>
        <DialogShellFooter
          leftSlot={
            <Button
              type="button"
              variant="ghost"
              size="small"
              wrap
              onClick={onCancel}
              loading={backInFlight}
              disabled={applyInFlight}
            >
              <ArrowLeft className="h-4 w-4 mr-1" />
              {t('stepRecording.review.back')}
            </Button>
          }
          primary={{
            label: isGenerating
              ? t('stepRecording.review.generating')
              : t('stepRecording.review.apply'),
            onClick: () =>
              onApply({
                name: nameField.value.trim(),
                description: descriptionField.value.trim()
              }),
            loading: isGenerating || applyInFlight,
            disabled: !canApply || applyInFlight || backInFlight
          }}
        />
      </DialogShell>
    </Dialog>
  );
}
